import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Play, ChevronDown, Plus, Trash2, Check, X, Volume2, VolumeX, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { VIDEO_MODEL_OPTIONS } from "@/lib/aiModels";
import { useGenerationCosts } from "@/hooks/useGenerationCosts";
import type { useVideoVersions } from "@/hooks/useVideoVersions";

type VideoApi = ReturnType<typeof useVideoVersions>;

/**
 * Toolbar cluster: the Create/Play/Close button plus the version picker.
 * Shared by the master-visual and deliverable lightboxes.
 */
export function VideoToolbarControls({
  video,
  className,
}: {
  video: VideoApi;
  /**
   * Trigger styling. Pass the overlay treatment when these sit on top of an
   * image (matching ImageActionMenu) so they don't read as transparent
   * against the artwork; omit it inside a solid toolbar.
   */
  className?: string;
}) {
  const { hasVideos, isPlaying, isGenerating, activeVideoId, videos } = video;
  const triggerClass = className || "h-8 text-xs";

  return (
    <>
      <Button
        size="sm"
        variant={hasVideos ? "secondary" : "outline"}
        className={cn("gap-1.5", triggerClass)}
        onClick={() => {
          if (isPlaying) video.closePlayback();
          else if (hasVideos && activeVideoId) video.play(activeVideoId);
          else video.setShowPrompt(true);
        }}
        disabled={isGenerating}
      >
        {isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
        {isGenerating ? "Generating..." : isPlaying ? "Close Video" : hasVideos ? "Play Video" : "Create Video"}
      </Button>

      {hasVideos && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className={cn("px-1.5", triggerClass)} disabled={isGenerating}>
              <ChevronDown className="w-3.5 h-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            <DropdownMenuLabel>Video versions ({videos.length})</DropdownMenuLabel>
            {videos.map((v, i) => (
              <div
                key={v.id}
                className={cn(
                  "flex items-center gap-1 px-1.5 py-1 rounded-sm text-sm",
                  activeVideoId === v.id ? "bg-accent" : "hover:bg-accent/50",
                )}
              >
                <button
                  className="flex items-center gap-2 flex-1 min-w-0 text-left"
                  onClick={() => video.play(v.id)}
                >
                  <span className="w-4 flex-shrink-0">
                    {activeVideoId === v.id && <Check className="w-3.5 h-3.5" />}
                  </span>
                  <span className="truncate">{v.prompt || `Version ${i + 1}`}</span>
                </button>
                <button
                  className="w-6 h-6 flex items-center justify-center flex-shrink-0 text-muted-foreground hover:text-destructive transition-colors"
                  onClick={() => video.deleteVersion(v.id)}
                  aria-label="Delete this version"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => video.setShowPrompt(true)}>
              <Plus className="w-4 h-4 mr-2" />
              New video…
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </>
  );
}

/**
 * The video itself, overlaid on the still image, plus mute/close controls.
 * Render inside a `relative` container that wraps the image.
 */
export function VideoPlayerOverlay({
  video,
  formatWidth,
  formatHeight,
}: {
  video: VideoApi;
  formatWidth?: number;
  formatHeight?: number;
}) {
  const { isPlaying, activeVideo, isMuted } = video;
  if (!isPlaying || !activeVideo) return null;

  const ratioStyle = formatWidth && formatHeight
    ? { aspectRatio: `${formatWidth}/${formatHeight}` }
    : {};

  return (
    <>
      <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/90 rounded-lg">
        <div
          className="relative overflow-hidden rounded-lg"
          style={{ ...ratioStyle, maxWidth: "100%", maxHeight: "100%" }}
        >
          <video
            key={activeVideo.id}
            src={activeVideo.url}
            autoPlay
            loop
            playsInline
            muted={isMuted}
            className="w-full h-full object-cover"
          />
        </div>
      </div>

      <div className="absolute top-3 right-3 z-[70] flex items-center gap-1.5">
        <button
          onClick={() => video.setIsMuted(!isMuted)}
          className="w-8 h-8 rounded-full bg-background/80 hover:bg-background flex items-center justify-center transition-colors"
          aria-label={isMuted ? "Unmute video" : "Mute video"}
          title={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        </button>
        <button
          onClick={video.closePlayback}
          className="w-8 h-8 rounded-full bg-background/80 hover:bg-background flex items-center justify-center transition-colors"
          aria-label="Close video"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}

/** "Create Video" dialog: motion prompt, recent prompts, and model choice. */
export function VideoPromptDialog({ video }: { video: VideoApi }) {
  const { showPrompt, promptText, recentPrompts, model, error } = video;
  const { costOf } = useGenerationCosts();

  return (
    <Dialog open={showPrompt} onOpenChange={video.setShowPrompt}>
      <DialogContent className="sm:max-w-md">
        <DialogTitle>Create Video</DialogTitle>
        <p className="text-sm text-muted-foreground">
          Describe background/object motion only (for example curtains, light, shadows).
          Instructions about people/faces are ignored to prevent content-policy blocks.
        </p>
        <Textarea
          placeholder="e.g. Curtains sway gently, light shifts slowly across the wall..."
          value={promptText}
          onChange={(e) => video.setPromptText(e.target.value)}
          rows={4}
          className="mt-2"
        />

        {recentPrompts.length > 0 && (
          <div className="mt-2 space-y-1">
            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Recent</span>
            <div className="flex flex-wrap gap-1.5">
              {recentPrompts.map((p, i) => (
                <button
                  key={i}
                  className="text-xs px-2.5 py-1 rounded-full border border-border bg-muted/50 hover:bg-primary/10 hover:border-primary/30 text-muted-foreground hover:text-foreground transition-colors truncate max-w-[200px]"
                  onClick={() => video.setPromptText(p)}
                  title={p}
                >
                  {p.length > 40 ? p.slice(0, 40) + "…" : p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cost varies ~8x across these, so show the estimate up front */}
        <div className="mt-3 space-y-1.5">
          <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Model</span>
          <div className="grid grid-cols-3 gap-1.5">
            {VIDEO_MODEL_OPTIONS.map((m) => (
              <button
                key={m.id}
                onClick={() => video.setModel(m.id)}
                className={cn(
                  "flex flex-col items-start gap-0.5 px-2.5 py-2 rounded-lg border text-left transition-colors",
                  model === m.id
                    ? "border-primary bg-primary/10"
                    : "border-border bg-secondary hover:border-primary/30",
                )}
              >
                <span className="text-xs font-medium">{m.label}</span>
                <span className="text-[10px] text-muted-foreground leading-tight">{m.blurb}</span>
                <span className="text-[10px] font-medium text-primary">{costOf(m.costKey, 0)} credits</span>
              </button>
            ))}
          </div>
        </div>

        {error && <p className="mt-2 text-xs text-destructive">{error}</p>}

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" size="sm" onClick={() => video.setShowPrompt(false)}>
            Cancel
          </Button>
          <Button
            size="sm"
            className="gradient-primary text-primary-foreground"
            onClick={() => {
              video.generate(promptText);
              video.setPromptText("");
            }}
          >
            <Play className="w-3.5 h-3.5 mr-1.5" />
            Generate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
